import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  experienceStorage,
  STORAGE_KEYS,
} from '@experiences/blocs/utils/storage';

export type CopilotMessageRole = 'user' | 'copilot';

export enum CopilotMessageStatus {
  Failed = 'failed',
  Sending = 'sending',
  Sent = 'sent',
}

export interface CopilotMessage {
  id: string;
  role: CopilotMessageRole;
  status: CopilotMessageStatus;
  text: string;
  timestamp: string;
}

// Session TTL in milliseconds
const THREE_DAYS = 3 * 24 * 60 * 60 * 1000;

const isExpired = (lastActivityDate?: string): boolean => {
  if (!lastActivityDate) {
    return false;
  }
  const last = Date.parse(lastActivityDate);
  if (Number.isNaN(last)) {
    return true;
  }
  return Date.now() - last > THREE_DAYS;
};

interface CopilotSessionState {
  generation: number;
  lastActivityDate?: string;
  messages: CopilotMessage[];
  sessionId?: string;
}

interface CopilotSessionActions {
  /**
   * Append messages to the end of the conversation and bump last activity.
   *
   * @param messages - Messages to add, in display order.
   */
  appendMessages: (messages: CopilotMessage[]) => void;
  /** Clear the chat. */
  reset: () => void;
  /**
   * Set the active Copilot session id.
   *
   * The session ID is how Copilot maintains context between messages.
   *
   * @param sessionId - Session id returned by the backend.
   */
  setSessionId: (sessionId: string) => void;
  /**
   * Update a message; used to change the status of a message, e.g. from
   * `Sending` to `Sent` or `Failed`.
   *
   * @param id - `id` of the message to update.
   * @param patch - Partial fields to merge onto the message.
   */
  updateMessage: (id: string, patch: Partial<CopilotMessage>) => void;
}

type CopilotSessionStore = CopilotSessionState & CopilotSessionActions;

const INITIAL_STATE: CopilotSessionState = {
  generation: 0,
  lastActivityDate: undefined,
  messages: [],
  sessionId: undefined,
};

/**
 * Zustand store for maintaining the Copilot conversation.
 *
 * Persists the messages and session ID (which allows Pepper GPT to
 * maintain context between messages). Resets the session after 3
 * days of inactivity.
 */
export const useCopilotSessionStore = create<CopilotSessionStore>()(
  persist(
    (set) => ({
      ...INITIAL_STATE,
      appendMessages: (messages) =>
        set((state) => ({
          lastActivityDate: new Date().toISOString(),
          messages: [...state.messages, ...messages],
        })),
      reset: () =>
        set((state) => ({
          ...INITIAL_STATE,
          generation: state.generation + 1,
        })),
      setSessionId: (sessionId) =>
        set({ lastActivityDate: new Date().toISOString(), sessionId }),
      updateMessage: (id, patch) =>
        set((state) => ({
          lastActivityDate: new Date().toISOString(),
          messages: state.messages.map((m) =>
            m.id === id ? { ...m, ...patch } : m,
          ),
        })),
    }),
    {
      name: STORAGE_KEYS.COPILOT_SESSION,
      onRehydrateStorage: () => (state) => {
        if (!state) {
          return;
        }

        // If the previous Copilot session hasn't been used for 3 days, clear it
        if (isExpired(state.lastActivityDate)) {
          state.reset();
          return;
        }

        // If we're rehydrating the store, the user isn't in the middle of chatting,
        // so any 'sending' messages are bugged and should be considered 'failed'
        if (
          state.messages.some((m) => m.status === CopilotMessageStatus.Sending)
        ) {
          useCopilotSessionStore.setState({
            messages: state.messages.map((m) =>
              m.status === CopilotMessageStatus.Sending
                ? { ...m, status: CopilotMessageStatus.Failed }
                : m,
            ),
          });
        }
      },
      partialize: (state) => ({
        lastActivityDate: state.lastActivityDate,
        messages: state.messages,
        sessionId: state.sessionId,
      }),
      storage: createJSONStorage(() => ({
        getItem: (key) => experienceStorage.getString(key) ?? null,
        removeItem: (key) => experienceStorage.remove(key),
        setItem: (key, value) => experienceStorage.set(key, value),
      })),
    },
  ),
);
